import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { Layout } from '@/components/layout/Layout';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { AcceptInvitePage } from '@/pages/auth/AcceptInvitePage';
import { PendingInvitationsPage } from '@/pages/auth/PendingInvitationsPage';
import { GoogleCallbackPage } from '@/pages/auth/GoogleCallbackPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { AgentsListPage } from '@/pages/agents/AgentsListPage';
import { AgentDetailPage } from '@/pages/agents/AgentDetailPage';
import { AgentWorkflowPage } from '@/pages/agents/AgentWorkflowPage';
import { CreateAgentPage } from '@/pages/agents/CreateAgentPage';
import { ConversationsPage } from '@/pages/conversations/ConversationsPage';
import { DocumentsPage } from '@/pages/documents/DocumentsPage';
import { KnowledgeBasePage } from '@/pages/knowledge/KnowledgeBasePage';
import { ChannelsPage } from '@/pages/channels/ChannelsPage';
import { MetaOAuthCallback } from '@/pages/channels/MetaOAuthCallback';
import { IntegrationsPage } from '@/pages/integrations/IntegrationsPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { UsagePage } from '@/pages/usage/UsagePage';
import { ApiDocsPage } from '@/pages/docs/ApiDocsPage';
import { NotificationsPage } from '@/pages/notifications/NotificationsPage';
import { BotsPage } from '@/pages/bots/BotsPage';
import { SimulationsPage } from '@/pages/simulations/SimulationsPage';
import { KnowledgeMapPage } from '@/pages/knowledge-map/KnowledgeMapPage';
import { TeamPage } from '@/pages/team/TeamPage';
import { TrainingListPage } from '@/pages/training/TrainingListPage';
import { TrainingDetailPage } from '@/pages/training/TrainingDetailPage';
import { TrainingSessionPage } from '@/pages/training/TrainingSessionPage';
import { TrainingReviewsPage } from '@/pages/training/TrainingReviewsPage';
import { SkillsPage } from '@/pages/skills/SkillsPage';
import { BillingPage } from '@/pages/billing/BillingPage';
import { SupportPage } from '@/pages/support/SupportPage';
import { ContactsPage } from '@/pages/contacts/ContactsPage';
import { SubmissionsPage } from '@/pages/submissions/SubmissionsPage';
import { OnboardingPage } from '@/pages/onboarding/OnboardingPage';
import { CoWorkPage } from '@/pages/cowork/CoWorkPage';
import { InboxPage } from '@/pages/inbox/InboxPage';
import { SocialCapturePage } from '@/pages/social-capture/SocialCapturePage';
import { SocialCaptureDetailPage } from '@/pages/social-capture/SocialCaptureDetailPage';
import { useAuthStore } from '@/store/authStore';
import { referralService } from '@/services/referral.service';
import { Spinner } from '@/components/common/Spinner';

const AdminLayout = React.lazy(() => import('@/pages/admin/AdminLayout'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!user?.is_super_admin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { fetchCurrentUser } = useAuthStore();

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  // Capture referral slug from URL param (?ref=xxx), validate it, store code
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      // Clean the URL immediately
      params.delete('ref');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // Validate slug and store the actual referral code
      referralService.validateSlug(ref).then((data) => {
        if (data.valid && data.code) {
          localStorage.setItem('referral_code', data.code);
        }
      }).catch(() => {
        // Invalid slug, ignore
      });
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage />}
          />
          <Route
            path="/invite/:token"
            element={<AcceptInvitePage />}
          />
          <Route
            path="/pending-invitations"
            element={
              <ProtectedRoute>
                <PendingInvitationsPage />
              </ProtectedRoute>
            }
          />

          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<DashboardPage />} />
            <Route path="/agents" element={<AgentsListPage />} />
            <Route path="/agents/new" element={<CreateAgentPage />} />
            <Route path="/agents/:id" element={<AgentDetailPage />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/inbox/:id" element={<InboxPage />} />
            <Route path="/conversations" element={<ConversationsPage />} />
            <Route path="/conversations/:id" element={<ConversationsPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/orders" element={<SubmissionsPage />} />
            <Route path="/submissions" element={<Navigate to="/orders" replace />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/knowledge" element={<KnowledgeBasePage />} />
            <Route path="/knowledge/:id" element={<KnowledgeBasePage />} />
            <Route path="/knowledge-map" element={<KnowledgeMapPage />} />
            <Route path="/channels" element={<ChannelsPage />} />
            <Route path="/channels/:id" element={<ChannelsPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/bots" element={<BotsPage />} />
            <Route path="/bots/:id" element={<BotsPage />} />
            <Route path="/simulations" element={<SimulationsPage />} />
            <Route path="/simulations/:id" element={<SimulationsPage />} />
            <Route path="/usage" element={<UsagePage />} />
            <Route path="/docs" element={<ApiDocsPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/training" element={<TrainingListPage />} />
            <Route path="/training/reviews" element={<TrainingReviewsPage />} />
            <Route path="/training/reviews/:id" element={<TrainingReviewsPage />} />
            <Route path="/training/:id" element={<TrainingDetailPage />} />
            <Route path="/training/session/:sessionId" element={<TrainingSessionPage />} />
            <Route path="/skills" element={<SkillsPage />} />
            <Route path="/skills/:id" element={<SkillsPage />} />
            <Route path="/social-capture" element={<SocialCapturePage />} />
            <Route path="/social-capture/:pageId" element={<SocialCaptureDetailPage />} />
            <Route path="/cowork" element={<CoWorkPage />} />
            <Route path="/cowork/:conversationId" element={<CoWorkPage />} />
            <Route path="/credits" element={<Navigate to="/billing" replace />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/admin/*" element={
              <SuperAdminRoute>
                <React.Suspense fallback={<div className="flex items-center justify-center p-12"><Spinner size="lg" /></div>}>
                  <AdminLayout />
                </React.Suspense>
              </SuperAdminRoute>
            } />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/profile" element={<SettingsPage />} />
          </Route>

          <Route
            path="/agents/:agentId/workflow/:workflowId"
            element={
              <ProtectedRoute>
                <AgentWorkflowPage />
              </ProtectedRoute>
            }
          />

          {/* Google OAuth callback */}
          <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />

          {/* Meta OAuth callback (opened in popup, no layout needed) */}
          <Route path="/channels/meta/callback" element={<MetaOAuthCallback />} />

          {/* Onboarding wizard (no layout, standalone page) */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </QueryClientProvider>
  );
};

export default App;
